import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Polygon, useJsApiLoader } from "@react-google-maps/api";
import { envConfig } from "@/shared/services/apiConfig";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import {
  cellIdToH3Index,
  getCellCenter,
  getMapViewForPoints,
  H3_MAP_DEFAULT_ZOOM,
  H3_MAP_MAX_ZOOM,
  H3_MAP_MIN_ZOOM,
  H3_MAP_SINGLE_POINT_ZOOM,
  H3CellIdInput,
} from "@/shared/utils/h3-map-utils";
import * as h3 from "h3-js";

export { cellIdToH3Index };
export type { H3CellIdInput };

export interface H3MapPolygonData {
  h3CellId: string;
  serviceLine?: ServiceLine;
}

interface H3MapProps {
  polygons: H3MapPolygonData[];
  height?: string;
  zoomLevel?: number;
  selectedPolygon: H3MapPolygonData | null;
  onPolygonSelect?: (polygonData: H3MapPolygonData) => void;
  /** Refit bounds when the map container resizes (e.g. inside a dialog). */
  fitOnResize?: boolean;
}

const SELECTED_POINT_ZOOM = 8;

export const H3Map = ({
  polygons,
  height = "300px",
  selectedPolygon,
  onPolygonSelect,
  fitOnResize = false,
}: H3MapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  // Caches to avoid expensive H3 -> boundary / center recomputation on every render.
  const h3BoundaryCacheRef = useRef<Map<string, google.maps.LatLngLiteral[]>>(new Map());
  const h3CenterCacheRef = useRef<Map<string, google.maps.LatLngLiteral>>(new Map());
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 0, lng: 0 });
  const [mapZoom, setMapZoom] = useState<number>(H3_MAP_DEFAULT_ZOOM);
  const [isMapReady, setIsMapReady] = useState(false);
  const pendingAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIdleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const animationSeqRef = useRef(0);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: envConfig.googleMapsApiKey,
  });

  const googleMapOptions = useMemo(() => {
    return {
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      minZoom: 2,
      restriction: {
        latLngBounds: {
          north: 85,
          south: -85,
          west: -180,
          east: 180,
        },
        strictBounds: true,
      },
    };
  }, []);

  const polygonOptionsSelected = useMemo(() => {
    const zoom = mapZoom;
    const isZoomedOut = zoom <= 6;
    const isZoomedIn = zoom >= 8;

    return {
      fillColor: "#2563eb",
      fillOpacity: isZoomedOut ? 0.85 : isZoomedIn ? 0.82 : 0.9,
      strokeColor: "#1d4ed8",
      strokeWeight: isZoomedOut ? 4 : isZoomedIn ? 2 : 2.5,
      clickable: Boolean(onPolygonSelect),
      zIndex: 2,
    } as google.maps.PolygonOptions;
  }, [mapZoom, onPolygonSelect]);

  const polygonOptionsDefault = useMemo(() => {
    const zoom = mapZoom;
    const isZoomedOut = zoom <= 6;
    const isZoomedIn = zoom >= 8;

    return {
      fillColor: "#16a34a",
      fillOpacity: isZoomedOut ? 0.65 : isZoomedIn ? 0.28 : 0.5,
      strokeColor: "#15803d",
      strokeWeight: isZoomedOut ? 3 : isZoomedIn ? 1 : 1.5,
      clickable: Boolean(onPolygonSelect),
      zIndex: 1,
    } as google.maps.PolygonOptions;
  }, [mapZoom, onPolygonSelect]);

  const hexagons = useMemo(() => {
    if (!polygons?.length) return [];

    return polygons.map((polygonData) => {
      try {
        const cachedBoundary = h3BoundaryCacheRef.current.get(polygonData.h3CellId);
        const boundary =
          cachedBoundary ??
          (() => {
            const h3Index = cellIdToH3Index(polygonData.h3CellId);
            const points = h3.cellToBoundary(h3Index);
            const path = points.map(([lat, lng]) => ({ lat, lng }));
            h3BoundaryCacheRef.current.set(polygonData.h3CellId, path);
            return path;
          })();

        return {
          polygonData,
          path: boundary,
        };
      } catch {
        return null;
      }
    }).filter((hex): hex is { polygonData: H3MapPolygonData; path: google.maps.LatLngLiteral[] } => Boolean(hex));
  }, [polygons]);

  const uniqueCenters = useMemo<google.maps.LatLngLiteral[]>(() => {
    if (!polygons?.length) return [];

    const seen = new Set<string>();
    const centers: google.maps.LatLngLiteral[] = [];
    for (const polygonData of polygons) {
      if (seen.has(polygonData.h3CellId)) continue;
      seen.add(polygonData.h3CellId);
      const point = getCellCenter(polygonData.h3CellId, h3CenterCacheRef.current);
      if (point) centers.push(point);
    }
    return centers;
  }, [polygons]);

  const mapView = useMemo(() => getMapViewForPoints(uniqueCenters), [uniqueCenters]);

  const fitMapToCenters = useCallback(() => {
    if (!mapRef.current || !uniqueCenters.length || selectedPolygon) return;

    const applyMapView = (targetCenter: google.maps.LatLngLiteral, zoom: number) => {
      mapRef.current!.setCenter(targetCenter);
      mapRef.current!.setZoom(zoom);
      setCenter(targetCenter);
      setMapZoom(zoom);
    };

    if (uniqueCenters.length === 1) {
      applyMapView(uniqueCenters[0], H3_MAP_SINGLE_POINT_ZOOM);
      return;
    }

    if (mapView.isWideSpread) {
      applyMapView(mapView.center, mapView.zoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    uniqueCenters.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds, 40);

    google.maps.event.addListenerOnce(mapRef.current, "idle", () => {
      if (!mapRef.current) return;

      let finalZoom = mapRef.current.getZoom() ?? H3_MAP_MIN_ZOOM;
      const finalCenter = mapView.center;

      if (finalZoom > H3_MAP_MAX_ZOOM) {
        finalZoom = H3_MAP_MAX_ZOOM;
      }

      if (finalZoom < H3_MAP_MIN_ZOOM) {
        finalZoom = H3_MAP_MIN_ZOOM;
      }

      mapRef.current.setCenter(finalCenter);
      mapRef.current.setZoom(finalZoom);
      setCenter(finalCenter);
      setMapZoom(finalZoom);
    });
  }, [mapView, selectedPolygon, uniqueCenters]);

  // Seed a sensible center before the map loads so it does not flash at (0, 0).
  useEffect(() => {
    if (uniqueCenters.length > 0 && !isMapReady) {
      setCenter(mapView.center);
    }
  }, [isMapReady, mapView.center, uniqueCenters.length]);

  useEffect(() => {
    if (!isMapReady) return;
    fitMapToCenters();
  }, [fitMapToCenters, isMapReady]);

  useEffect(() => {
    if (!fitOnResize || !isMapReady) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      if (!mapRef.current) return;
      google.maps.event.trigger(mapRef.current, "resize");
      fitMapToCenters();
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [fitMapToCenters, fitOnResize, isMapReady]);

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return;
    if (!selectedPolygon) return;

    // Only trigger the animation when the selected polygon id changes.
    if (pendingAnimationTimeoutRef.current) {
      clearTimeout(pendingAnimationTimeoutRef.current);
      pendingAnimationTimeoutRef.current = null;
    }
    if (pendingIdleListenerRef.current) {
      pendingIdleListenerRef.current.remove();
      pendingIdleListenerRef.current = null;
    }
    const animationSeq = ++animationSeqRef.current;

    let targetCenter: google.maps.LatLngLiteral | null = null;
    try {
      const h3Index = cellIdToH3Index(selectedPolygon.h3CellId);
      const [lat, lng] = h3.cellToLatLng(h3Index);
      targetCenter = { lat, lng };
    } catch {
      // Ignore invalid selected polygon ids.
    }

    if (!targetCenter) return;

    // "Pan out then pan in":
    // 1) zoom out at current center (avoid long pan to an average center)
    // 2) zoom back in + pan to the selected polygon center
    const zoomOutLevel = Math.max(2, SELECTED_POINT_ZOOM - 2);

    // Step 1: out
    setMapZoom(zoomOutLevel);
    mapRef.current.setZoom(zoomOutLevel);

    const panIn = () => {
      if (animationSeqRef.current !== animationSeq) return; // selection changed mid-animation

      if (pendingAnimationTimeoutRef.current) {
        clearTimeout(pendingAnimationTimeoutRef.current);
        pendingAnimationTimeoutRef.current = null;
      }

      // Step 2: in
      setMapZoom(SELECTED_POINT_ZOOM);
      setCenter(targetCenter!);
      mapRef.current?.setZoom(SELECTED_POINT_ZOOM);
      mapRef.current?.panTo(targetCenter!);
    };

    // Step 2: start when map settles after pan-out (avoids jerky fixed timing).
    pendingIdleListenerRef.current = google.maps.event.addListenerOnce(mapRef.current, "idle", panIn);

    // Fallback: if `idle` doesn't fire for some reason, run step 2 after a reasonable time.
    pendingAnimationTimeoutRef.current = setTimeout(() => {
      pendingAnimationTimeoutRef.current = null;
      panIn();
    }, 1500);

    return () => {
      if (pendingAnimationTimeoutRef.current) {
        clearTimeout(pendingAnimationTimeoutRef.current);
        pendingAnimationTimeoutRef.current = null;
      }
      if (pendingIdleListenerRef.current) {
        pendingIdleListenerRef.current.remove();
        pendingIdleListenerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, selectedPolygon?.h3CellId]);

  if (!isLoaded) {
    return (
      <div
        className="flex items-center justify-center border rounded-md"
        style={{ height }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full rounded-md border" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={mapZoom}
        options={googleMapOptions}
        onLoad={(map) => {
          mapRef.current = map;
          setMapZoom(H3_MAP_DEFAULT_ZOOM);
          setIsMapReady(true);
        }}
        onZoomChanged={() => {
          if (!mapRef.current) return;
          const currentZoom = mapRef.current.getZoom();
          if (typeof currentZoom === "number" && currentZoom !== mapZoom) {
            setMapZoom(currentZoom);
          }
        }}
      >
        {hexagons.map(({ path, polygonData }, index) => {
          const isSelected = polygonData.h3CellId === selectedPolygon?.h3CellId;
          return (
            <Polygon
              key={`${polygonData.serviceLine?.serviceLineNumber ?? index.toString()}-${polygonData.h3CellId}`}
              path={path}
              onClick={() => onPolygonSelect?.(polygonData)}
              options={isSelected ? polygonOptionsSelected : polygonOptionsDefault}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
};